import { useState, useEffect, useMemo } from 'react';
import Cookies from 'universal-cookie';
import logger from '@helpers/utils/logger/client';
import useUser from '@hooks/user/use-user';
import { useBucket } from '@context/bucket.context';

const ANONYMOUS_EXPIRY_DAYS = 7;
const USER_EXPIRY_DAYS = 365;
const NEW_USER_THRESHOLD_DAYS = 31;

// TODO: Remove use-mmao-onboarding-tooltip.ts once it's pushed to production
interface useInfoPopoverOptions {
  cookieString: string;
  onlyForNewUsers?: boolean;
}

interface CookieOptions {
  path: string;
  sameSite: 'strict' | 'lax' | 'none';
  maxAge?: number;
}

export const useInfoPopover = ({ cookieString, onlyForNewUsers = false }: useInfoPopoverOptions) => {
  const { isAuthenticated, user } = useUser();
  const { anonymousId } = useBucket();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isNewUser = useMemo(() => {
    if (!isClient) {
      return false;
    }

    if (!isAuthenticated && !user?.id && anonymousId) {
      return true;
    }

    if (!user?.inscriptionDate) {
      return false;
    }

    const inscriptionDate = new Date(user.inscriptionDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - inscriptionDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= NEW_USER_THRESHOLD_DAYS;
  }, [isClient, isAuthenticated, user?.inscriptionDate, anonymousId, user?.id]);

  const hasSeenTooltipCookie = useMemo(() => {
    if (!isClient) {
      return true;
    }

    const cookies = new Cookies();
    try {
      return cookies.get(cookieString) === 1;
    } catch (error) {
      logger.error(`Error checking cookie ${cookieString}:`, error);
      return true;
    }
  }, [isClient, cookieString]);

  const shouldShowTooltip = useMemo(() => {
    if (!isClient) {
      return false;
    }

    if (onlyForNewUsers) {
      const userShouldSee = !isAuthenticated || isNewUser;
      return !hasSeenTooltipCookie && userShouldSee;
    } else {
      return !hasSeenTooltipCookie;
    }
  }, [isClient, hasSeenTooltipCookie, isAuthenticated, isNewUser, onlyForNewUsers]);

  const handleTooltipDismiss = () => {
    if (!isClient) {
      return;
    }

    const cookies = new Cookies();
    const cookieOptions: CookieOptions = {
      path: '/',
      sameSite: 'lax',
    };

    if (onlyForNewUsers) {
      const expiryDays = isAuthenticated && user?.id ? USER_EXPIRY_DAYS : ANONYMOUS_EXPIRY_DAYS;
      cookieOptions.maxAge = expiryDays * 24 * 60 * 60;
    }

    try {
      cookies.set(cookieString, 1, cookieOptions);
    } catch (error) {
      logger.error(`Error setting tooltip cookie ${cookieString}:`, error);
    }
  };

  return {
    hasSeenOnboardingTooltip: hasSeenTooltipCookie,
    handleTooltipDismiss,
    isTooltipEnabled: shouldShowTooltip,
  };
};
